import { useState } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api';
import useAuthStore from '../stores/authStore';
import useConnectionStore from '../stores/connectionStore';
import './ConnectionButtons.css';

const ConnectionButtons = ({ candidateSlug }) => {
  const token = useAuthStore((state) => state.token);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const getConnections = useConnectionStore((state) => state.getCandidateState);
  const updateConnectionInStore = useConnectionStore((state) => state.updateCandidateState);
  const fetchConnectionProfiles = useConnectionStore((state) => state.fetchConnectionProfiles);
  useConnectionStore((state) => state.candidateStates); // Subscribe to trigger re-render when states change
  
  const [isUpdating, setIsUpdating] = useState(false);
  const [updatingAction, setUpdatingAction] = useState(null);
  
  const connection = getConnections(candidateSlug);

  const updateConnection = async (action) => {
    if (!isAuthenticated()) {
      alert('Please sign in to use this feature');
      return;
    }

    setIsUpdating(true);
    setUpdatingAction(action);
    
    try {
      const response = await axios.post(
        `${API_CONFIG.REST_URI}/connections/update`,
        { slug: candidateSlug, action },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        updateConnectionInStore(candidateSlug, response.data.state);
        // Refetch connection profiles silently in background (no loading spinner)
        fetchConnectionProfiles(token, true);
      }
    } catch (error) {
      // Handle 401 Unauthorized
      if (error.response?.status === 401) {
        const { default: useAuthStoreModule } = await import('../stores/authStore');
        // Clear token silently without redirecting
        const state = useAuthStoreModule.getState();
        state.token = null;
        state.user = null;
        return;
      }
      console.error('Error updating connection:', error);
      alert(error.response?.data?.error || 'Failed to update connection');
    } finally {
      setIsUpdating(false);
      setUpdatingAction(null);
    }
  };

  const renderButtonContent = (type, activeIcon, inactiveIcon) => {
    if (isUpdating && (
      (type === 'favorite' && (updatingAction === 'favorite' || (connection === 'favorite' && updatingAction === 'remove'))) ||
      (type === 'request_sent' && (updatingAction === 'request_sent' || (connection === 'request_sent' && updatingAction === 'remove'))) ||
      (type === 'connected' && (updatingAction === 'connected' || (connection === 'connected' && updatingAction === 'remove')))
    )) {
      return <span className="loading-dots">...</span>;
    }
    return connection === type ? activeIcon : inactiveIcon;
  };

  return (
    <div className="connection-buttons" onClick={(e) => e.stopPropagation()}>
      <button
        className={`connection-btn ${connection === 'favorite' ? 'active' : ''}`}
        onClick={() => updateConnection(connection === 'favorite' ? 'remove' : 'favorite')}
        title="Favorite"
        disabled={isUpdating}
      >
        {renderButtonContent(
          'favorite',
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
          </svg>,
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
          </svg>
        )}
      </button>
      <button
        className={`connection-btn ${connection === 'request_sent' ? 'active' : ''}`}
        onClick={() => updateConnection(connection === 'request_sent' ? 'remove' : 'request_sent')}
        title="Request Sent"
        disabled={isUpdating}
      >
        {renderButtonContent(
          'request_sent',
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
          </svg>,
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
            <polyline points="22,6 12,13 2,6"/>
          </svg>
        )}
      </button>
      <button
        className={`connection-btn ${connection === 'connected' ? 'active' : ''}`}
        onClick={() => updateConnection(connection === 'connected' ? 'remove' : 'connected')}
        title="Connected"
        disabled={isUpdating}
      >
        {renderButtonContent(
          'connected',
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>,
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
          </svg>
        )}
      </button>
    </div>
  );
};

export default ConnectionButtons;
